/**
 * useClarifyUpgrade — the authored question, arriving after the provisional one.
 *
 * The gate answers a send in milliseconds with the question it can write
 * deterministically, so the user is never staring at nothing (§R4). A moment
 * later the engine may emit `research.clarify` carrying the AUTHORED question:
 * same hold, same pending plan, same round, better prose and real options.
 * This hook is the subscription that delivers it.
 *
 * It is deliberately NOT part of `useResearchRun`:
 *
 *   * A pending question has no run. Opening a run subscription for it would
 *     put a spinner on a turn that is waiting on the USER, and would make the
 *     timeout budget start counting against a question nobody has answered.
 *   * The L2-B hold works the other way — a turn that IS running comes back to
 *     `awaiting_clarification` — and that event arrives on the run's own
 *     correlation id, which this hook subscribes to just the same.
 *
 * The hub listener is a wildcard, so the correlation filter is load-bearing,
 * exactly as it is in `useResearchRun`: without it a concurrent chat's
 * upgrade rewrites this question.
 */

import { useEffect, useRef } from 'react';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import { ClarifyEvent, isClarifyEvent } from './clarify';

/**
 * @param correlationId  the pending turn's correlation, or null to not listen.
 * @param onUpgrade      called with the event; must be safe to call twice, as
 *                       a reconnect can redeliver.
 */
export function useClarifyUpgrade(
  correlationId: string | null,
  onUpgrade: (event: ClarifyEvent) => void
): void {
  // The latest callback without re-subscribing on every render — a resubscribe
  // per render is how a hub listener leaks.
  const handlerRef = useRef(onUpgrade);
  handlerRef.current = onUpgrade;

  useEffect(() => {
    if (!correlationId) return;

    const service = getMultiHubSocketService();
    const listener = (data: any, eventName: string) => {
      if (!isClarifyEvent(eventName)) return;
      const eventCorrelationId = data?.correlation_id || data?.correlationId;
      if (eventCorrelationId !== correlationId) return;
      // The engine nests the question set under `data` like every other
      // research event; a flat payload is accepted too so one malformed
      // envelope cannot swallow the upgrade.
      const payload = data?.data && typeof data.data === 'object' ? data.data : data;
      handlerRef.current({
        ...payload,
        correlation_id: eventCorrelationId,
        message_uuid: payload?.message_uuid || data?.message_uuid,
      });
    };

    let attached = false;
    let cancelled = false;

    service
      .connectToHub('ai_processing')
      .then(() => {
        if (cancelled) return;
        service.on('ai_processing', '*', listener);
        attached = true;
      })
      .catch(() => {
        /* No upgrade, then. The provisional question stands and is answerable
           — the same discipline the engine applies on its side (§2.6 rule 6). */
      });

    return () => {
      cancelled = true;
      if (attached) service.off('ai_processing', '*', listener);
    };
  }, [correlationId]);
}

export default useClarifyUpgrade;
